/*
  NG Rabattkampagne – zentrale Logik (ersetzt nordseegut-promo-cards.js, nordseegut-bundle-picker.js,
  nordseegut-bundle-quick-add.js, die Inline-Skripte der Produkt-Templates und cart-discount-codes-custom.js).

  - Konfiguration: <script id="ng-discount-config"> aus snippets/ng-discount-promo.liquid (mode: 'config')
    mit allen nicht beendeten Rabattaktionen (Metaobjekt „Rabattaktion“): { code, start, end, pct: { gin, … } }
  - Laufende Aktion wird hier mit der Browserzeit bestimmt (Liquid hat sie mit der Serverzeit bereits sichtbar
    gerendert; das JS korrigiert nur zwischengespeicherte, veraltete Seiten). Prozent je Element über
    data-…-category (gin|kuemmel|absacker|vodka|sets).
  - Status: Shopify Cart AJAX API – GET /cart.js (discount_codes), POST /cart/update.js { discount: 'A,B' }
  - Hooks im Markup: ausschließlich data-ng-discount-*
  - Rabattformel (wie im Checkout): preis − floor(preis × prozent / 100), alle Werte in Cent
*/
(() => {
  if (window.NgDiscount) return;

  const config = readConfig();
  const CAMPAIGNS = (Array.isArray(config.campaigns) ? config.campaigns : [])
    .map((entry) => ({
      code: String((entry && entry.code) || '').trim(),
      start: Number(entry && entry.start) || 0,
      end: entry && entry.end ? Number(entry.end) : null,
      pct: (entry && entry.pct) || {},
      announcement: String((entry && entry.announcement) || '').trim(),
      announcementLink: String((entry && entry.announcementLink) || '').trim(),
    }))
    .filter((entry) => entry.code !== '' && entry.start > 0);
  let boundaryTimer = null;
  const T = Object.assign(
    {
      badge: '−[prozent] % MIT CODE',
      badgeActive: '−[prozent] % AKTIV',
      uvp: 'UVP',
      pdpLoading: 'Wird angewendet …',
      singleBought: 'Einzeln gekauft:',
      savings: 'Du sparst',
      savingsPercent: '% SPAREN',
      cartErrorNoCode: 'Bitte gib einen Rabattcode ein.',
      cartErrorAlreadyApplied: 'Dieser Rabattcode ist bereits aktiv.',
      cartErrorInvalid: 'Dieser Rabattcode ist ungültig oder nicht anwendbar.',
    },
    config.texts || {}
  );

  const SELECTORS = {
    card: '[data-ng-discount-card]',
    badge: '[data-ng-discount-badge]',
    cardBundle: '[data-ng-discount-card-bundle]',
    pdp: '[data-ng-discount-pdp]',
    picker: '[data-ng-discount-bundle-picker]',
    modal: '[data-ng-discount-bundle-modal]',
    savings: '[data-ng-savings]',
  };
  const WATCHED = Object.values(SELECTORS).join(', ');

  const state = { codes: [] };
  const cardState = new WeakMap();
  const pdpState = new WeakMap();
  let syncInFlight = null;
  let observerTimer = null;

  /* ---------- Hilfsfunktionen ---------- */

  function readConfig() {
    try {
      const node = document.getElementById('ng-discount-config');
      return node ? JSON.parse(node.textContent) : {};
    } catch (error) {
      return {};
    }
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function money(cents) {
    const value = Number(cents || 0);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(value);
    }
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value / 100);
  }

  // Code-Preis in Cent: EINZIGE Stelle der Rundung im Browser. Art aus der Theme-Einstellung „Rundung des Code-Rabatts“
  // (ng_rabatt_rundung → config.rounding): „abrunden“ (Standard, wie die Rabatt-App: Rabatt pro Stück auf den Cent
  // abgerundet, 10 % von 49,99 € = 4,99 €) oder „kaufmaennisch“. Liquid-Gegenstück: ng-discount-promo mode 'rabattpreis'.
  const ROUND_DISCOUNT = config.rounding === 'kaufmaennisch' ? Math.round : Math.floor;
  function promoPrice(price, percent) {
    const p = Number(price || 0);
    const pct = Number(percent || 0);
    if (!pct) return p;
    return p - ROUND_DISCOUNT((p * pct) / 100);
  }

  // Preis pro Flasche: kaufmännisch gerundet – identisch zu `| times: 1.0 | divided_by: qty | round` in Liquid.
  function perBottle(price, qty) {
    const count = Number(qty || 1);
    return Math.round(Number(price || 0) / (count > 0 ? count : 1));
  }

  // Grundpreis (PAngV) wie Shopify: Preis ÷ Menge in Bezugseinheit × Bezugsmenge, kaufmännisch gerundet.
  const UNIT_FACTORS = { ml: 0.001, cl: 0.01, dl: 0.1, l: 1, m3: 1000, mg: 0.001, g: 1, kg: 1000, mm: 0.001, cm: 0.01, m: 1, m2: 1, item: 1 };

  function unitPriceFor(price, measurement, fallback) {
    const m = measurement || {};
    const quantity = Number(m.quantity_value || 0);
    const reference = Number(m.reference_value || 1);
    const quantityFactor = UNIT_FACTORS[String(m.quantity_unit || '').toLowerCase()];
    const referenceFactor = UNIT_FACTORS[String(m.reference_unit || '').toLowerCase()];
    if (quantity > 0 && quantityFactor && referenceFactor) {
      return Math.round((Number(price || 0) / ((quantity * quantityFactor) / referenceFactor)) * reference);
    }
    // Unbekannte Einheit: proportional zum hinterlegten Grundpreis
    if (fallback && Number(fallback.unitPrice) > 0 && Number(fallback.price) > 0) {
      return Math.round((Number(fallback.unitPrice) * Number(price || 0)) / Number(fallback.price));
    }
    return 0;
  }

  function escapeHTML(text) {
    return String(text || '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
  }

  // Nur für Screenreader sichtbarer Text (wie Lumins price.liquid)
  function srOnly(text) {
    return text ? `<span class="visually-hidden visually-hidden--inline">${escapeHTML(text)}</span>` : '';
  }

  // Grundpreis-Markup wie in snippets/price.liquid (inkl. Screenreader-Labels)
  function unitPriceMarkup(amount, referenceValue, referenceUnit) {
    const reference = Number(referenceValue) !== 1 ? `${referenceValue} ${referenceUnit}` : referenceUnit;
    return `
      <small class="unit-price caption">
        ${T.srUnitPrice ? `<span class="visually-hidden">${escapeHTML(T.srUnitPrice)}</span>` : ''}
        <span class="price-item price-item--last">
          <span>${money(amount)}</span>
          <span aria-hidden="true"> / </span>
          ${T.srUnitSeparator ? `<span class="visually-hidden">&nbsp;${escapeHTML(T.srUnitSeparator)}&nbsp;</span>` : ''}
          <span>${escapeHTML(reference)}</span>
        </span>
      </small>
    `;
  }

  /* ---------- Rabattaktion ---------- */

  function nowSeconds() {
    return Math.floor(Date.now() / 1000);
  }

  // Laufende Aktion: start ≤ jetzt < ende (ende leer = unbegrenzt); bei mehreren die mit dem spätesten Start.
  function currentCampaign() {
    const now = nowSeconds();
    let best = null;
    CAMPAIGNS.forEach((entry) => {
      if (entry.start > now) return;
      if (entry.end !== null && entry.end <= now) return;
      if (!best || entry.start > best.start) best = entry;
    });
    return best;
  }

  function campaignCode() {
    const campaign = currentCampaign();
    return campaign ? campaign.code : '';
  }

  // Prozentwert der laufenden Aktion für eine Kategorie (leer/0 = kein Rabatt)
  function percentFor(category) {
    const campaign = currentCampaign();
    if (!campaign || !category) return 0;
    const value = Number(campaign.pct[category] || 0);
    return value > 0 ? value : 0;
  }

  // Seite bleibt offen: zum nächsten Start-/Endzeitpunkt neu rendern (max. 24 h voraus)
  function scheduleBoundary() {
    clearTimeout(boundaryTimer);
    const now = nowSeconds();
    const upcoming = [];
    CAMPAIGNS.forEach((entry) => {
      if (entry.start > now) upcoming.push(entry.start);
      if (entry.end !== null && entry.end > now) upcoming.push(entry.end);
    });
    if (!upcoming.length) return;
    const wait = (Math.min(...upcoming) - now) * 1000 + 1000;
    if (wait > 24 * 60 * 60 * 1000) return;
    boundaryTimer = setTimeout(() => {
      renderAll();
      scheduleBoundary();
    }, wait);
  }

  /* ---------- Ersparnis gegenüber Einzelkauf (snippets/ng-gebinde.liquid) ---------- */

  // Referenz „Einzeln gekauft“ aus data-parts = [[preis, "kategorie", anzahl], …].
  // Mit aktivem Code: jeder Bestandteil zum Code-Preis seiner Kategorie (Code-Preis gegen Code-Preis).
  // Bekommt das Set/Gebinde selbst keinen Code-Rabatt (keine Kategorie, z. B. ausverkauft), rechnen die Aufrufer
  // auch die Referenz ohne Code – sonst stünde ein Preis ohne Code gegen Code-Preise.
  function referenceFor(partsJSON, active) {
    let parts = [];
    try {
      parts = JSON.parse(partsJSON || '[]');
    } catch (error) {
      return 0;
    }
    if (!Array.isArray(parts)) return 0;
    return parts.reduce((sum, part) => {
      const price = Number(part[0] || 0);
      const count = Number(part[2] || 1);
      const unit = active ? promoPrice(price, percentFor(part[1])) : price;
      return sum + unit * count;
    }, 0);
  }

  // „/ Flasche“, „/ Fläschchen“ oder „/ Stück“ – Einheit kommt aus data-unit (snippets/ng-gebinde.liquid, mode unit)
  function perUnitLabel(node) {
    return `/ ${(node && node.dataset.unit) || 'Flasche'}`;
  }

  function savingsCopy(reference, savings) {
    return `${T.singleBought} ${money(reference)} · ${T.savings} ${money(savings)}`;
  }

  function renderSavings(active) {
    document.querySelectorAll(SELECTORS.savings).forEach((node) => {
      const price = Number(node.dataset.price || 0);
      const ownActive = active && percentFor(node.dataset.category) > 0;
      const effective = ownActive ? promoPrice(price, percentFor(node.dataset.category)) : price;
      const reference = referenceFor(node.dataset.parts, ownActive);
      const savings = reference - effective;
      node.hidden = !(savings > 0);
      if (savings <= 0) return;
      const referenceNode = node.querySelector('[data-ng-savings-reference]');
      const amountNode = node.querySelector('[data-ng-savings-amount]');
      if (referenceNode && referenceNode.textContent !== money(reference)) referenceNode.textContent = money(reference);
      if (amountNode && amountNode.textContent !== money(savings)) amountNode.textContent = money(savings);
    });
  }

  /* ---------- Ankündigungsleiste (sections/announcement-bar.liquid) ---------- */

  // Liquid hat mit der Serverzeit bereits den richtigen Text gerendert; hier nur bei veralteter Seite nachziehen.
  // Die Leiste reserviert mobil zwei Zeilen, ein Textwechsel ändert die Höhe nicht.
  function renderAnnouncement() {
    document.querySelectorAll('[data-ng-announcement]').forEach((root) => {
      const campaign = currentCampaign();
      const useCampaign = Boolean(campaign && campaign.announcement);
      const text = useCampaign ? campaign.announcement : root.dataset.defaultText || '';
      const link = useCampaign ? campaign.announcementLink || '' : root.dataset.defaultLink || '';
      const textNode = root.querySelector('[data-ng-announcement-text]');
      const linkNode = root.querySelector('[data-ng-announcement-link]');
      const arrow = root.querySelector('[data-ng-announcement-arrow]');
      if (textNode && textNode.textContent.trim() !== text.trim()) textNode.textContent = text;
      if (linkNode) {
        if (link && linkNode.getAttribute('href') !== link) linkNode.setAttribute('href', link);
        if (!link && linkNode.hasAttribute('href')) linkNode.removeAttribute('href');
      }
      if (arrow) arrow.hidden = !link;
    });
  }

  function fillTemplate(template, percent) {
    return String(template || '')
      .split('[prozent]').join(String(percent))
      .split('[code]').join(campaignCode());
  }

  function normalizeCodes(codes) {
    if (!Array.isArray(codes)) return [];
    return codes
      .map((entry) => ({
        code: String((entry && entry.code) || '').trim(),
        applicable: Boolean(entry && entry.applicable),
      }))
      .filter((entry) => entry.code !== '');
  }

  // Der Code der laufenden Aktion gilt als aktiv, sobald er im Warenkorb hinterlegt ist
  // (auch bei leerem Warenkorb, dort meldet Shopify applicable: false).
  function isActive() {
    const code = campaignCode().toUpperCase();
    return code !== '' && state.codes.some((entry) => entry.code.toUpperCase() === code);
  }

  /* ---------- Warenkorb-API ---------- */

  function setCodes(codes) {
    state.codes = normalizeCodes(codes);
    renderAll();
    document.dispatchEvent(
      new CustomEvent('ng:discount:change', { detail: { active: isActive(), codes: state.codes.slice() } })
    );
  }

  // Zeitpunkt der letzten Abfrage – Fokus fragt höchstens alle 60 s nach (siehe syncIfStale)
  const FOCUS_SYNC_INTERVAL = 60000;
  let lastSync = 0;

  function fetchState() {
    if (syncInFlight) return syncInFlight;
    lastSync = Date.now();
    syncInFlight = fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((cart) => setCodes(cart.discount_codes))
      .catch((error) => console.warn('Rabattstatus konnte nicht geladen werden.', error))
      .finally(() => {
        syncInFlight = null;
      });
    return syncInFlight;
  }

  function cartSectionTargets() {
    const targets = [];
    const seen = new Set();
    document.querySelectorAll('cart-drawer-items, cart-items').forEach((element) => {
      if (typeof element.getSectionsToRender !== 'function') return;
      let sections = [];
      try {
        sections = element.getSectionsToRender();
      } catch (error) {
        return;
      }
      sections.forEach((section) => {
        if (!section || !section.section || seen.has(section.id)) return;
        if (!document.getElementById(section.id)) return;
        seen.add(section.id);
        targets.push(section);
      });
    });
    return targets;
  }

  function applySections(sections, targets) {
    if (!sections) return;
    const parser = new DOMParser();
    targets.forEach((section) => {
      const html = sections[section.section];
      const container = document.getElementById(section.id);
      if (!html || !container) return;
      const source = parser.parseFromString(html, 'text/html').querySelector(section.selector);
      const target = container.querySelector(section.selector) || container;
      if (source) target.innerHTML = source.innerHTML;
    });
  }

  // Setzt die komplette Liste der Rabattcodes (Shopify ersetzt, nicht ergänzt).
  async function requestCodes(codes, { withSections = false } = {}) {
    const targets = withSections ? cartSectionTargets() : [];
    const body = { discount: codes.join(',') };
    if (targets.length) {
      body.sections = Array.from(new Set(targets.map((section) => section.section))).slice(0, 5);
      body.sections_url = window.location.pathname;
    }

    const promise = fetch(`${rootUrl()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    });

    // Shopify-Standardereignis, damit Apps auf Codeänderungen reagieren können.
    const event = new CustomEvent('shopify:cart:discount-update', {
      bubbles: true,
      detail: { source: 'ng-discount' },
    });
    event.discountCodes = codes.slice();
    event.promise = promise.then((cart) => ({
      cart: { discountCodes: normalizeCodes(cart.discount_codes) },
      userErrors: [],
      warnings: [],
      detail: { source: 'ng-discount' },
    }));
    event.promise.catch(() => {});
    document.dispatchEvent(event);

    const cart = await promise;
    setCodes(cart.discount_codes);
    return { cart, targets };
  }

  /* ---------- Produktkarten ---------- */

  function renderCardPrice(element, active) {
    if (!cardState.has(element)) {
      cardState.set(element, { html: element.innerHTML, className: element.className });
    }
    const saved = cardState.get(element);

    if (!active) {
      element.className = saved.className;
      element.innerHTML = saved.html;
      return;
    }

    const regularPrice = Number(element.dataset.ngDiscountRegularPrice || 0);
    const percent = percentFor(element.dataset.ngDiscountCategory);
    const uvpPrice = Number(element.dataset.ngDiscountUvpPrice || 0);
    if (!regularPrice || !percent) {
      element.className = saved.className;
      element.innerHTML = saved.html;
      return;
    }

    const discounted = promoPrice(regularPrice, percent);
    const priceVaries = element.dataset.ngDiscountPriceVaries === 'true';
    const fromTemplate = element.dataset.ngDiscountFromTemplate || '__PRICE__';
    const formattedPromo = money(discounted);
    // UVP nur, wenn der Vergleichspreis über dem normalen Preis liegt – sonst durchgestrichen der eigene Preis ohne Label
    const hasUvp = uvpPrice > regularPrice;
    const referencePrice = hasUvp ? uvpPrice : regularPrice;
    const promoDisplay = priceVaries ? fromTemplate.replace('__PRICE__', formattedPromo) : formattedPromo;

    const unitPrice = Number(element.dataset.ngDiscountUnitPrice || 0);
    const referenceValue = Number(element.dataset.ngDiscountUnitReferenceValue || 1);
    const referenceUnit = element.dataset.ngDiscountUnitReferenceUnit || '';
    let unitPriceHTML = '';

    if (unitPrice > 0 && referenceUnit) {
      const promoUnitPrice = unitPriceFor(
        discounted,
        {
          quantity_value: element.dataset.ngDiscountUnitQuantityValue,
          quantity_unit: element.dataset.ngDiscountUnitQuantityUnit,
          reference_value: referenceValue,
          reference_unit: referenceUnit,
        },
        { unitPrice, price: regularPrice }
      );
      unitPriceHTML = unitPriceMarkup(promoUnitPrice, referenceValue, referenceUnit);
    }

    element.className = `${saved.className} price--on-sale nsg-card-promo--active`;
    element.innerHTML = `
      <div class="price__container">
        <div class="nsg-card-promo__row">
          ${srOnly(T.srSalePrice)}
          <span class="price-item price-item--sale price-item--last nsg-card-promo__current">
            ${promoDisplay}
          </span>
          ${srOnly(T.srRegularPrice)}
          <s class="price-item price-item--regular nsg-card-promo__regular">
            ${hasUvp ? `${T.uvp} ` : ''}${money(referencePrice)}
          </s>
        </div>
        ${unitPriceHTML}
      </div>
    `;
  }

  function renderCards(active) {
    document.querySelectorAll(SELECTORS.card).forEach((element) => renderCardPrice(element, active));

    // Badges rendert Liquid nur bei laufender Aktion. Ist sie auf einer zwischengespeicherten Seite inzwischen
    // abgelaufen, wird das Element entfernt (nicht nur versteckt: .badge-custom setzt display und überstimmt hidden).
    document.querySelectorAll(SELECTORS.badge).forEach((badge) => {
      const percent = percentFor(badge.dataset.ngDiscountCategory);
      const label = badge.querySelector('[data-ng-discount-badge-label]');
      if (!percent) {
        badge.remove();
        return;
      }
      if (!label) return;
      const text = fillTemplate(active ? T.badgeActive : T.badge, percent);
      if (label.textContent !== text) label.textContent = text;
    });

    document.querySelectorAll(SELECTORS.cardBundle).forEach((node) => {
      const price = Number(node.dataset.bundlePrice || 0);
      const qty = Number(node.dataset.bundleQty || 1);
      const percent = percentFor(node.dataset.bundleCategory);
      if (!price || !qty) return;
      const effective = active ? promoPrice(price, percent) : price;
      const priceNode = node.querySelector('[data-ng-discount-best-bottle-price]');
      if (priceNode) priceNode.textContent = money(perBottle(effective, qty));
    });
  }

  /* ---------- Produktseite: Hinweisbox + Preisblock ---------- */

  function pdpController(root) {
    if (pdpState.has(root)) return pdpState.get(root);

    const productInfo = root.closest('product-info');
    const controller = {
      root,
      sectionId: productInfo ? productInfo.dataset.section : undefined,
      category: root.dataset.category || '',
      get percent() {
        return percentFor(this.category);
      },
      variant: {
        available: root.dataset.initialAvailable !== 'false',
        price: Number(root.dataset.initialPrice || 0),
        compare_at_price: Number(root.dataset.initialUvp || 0),
        unit_price: Number(root.dataset.initialUnitPrice || 0),
        unit_price_measurement: {
          quantity_value: Number(root.dataset.unitQuantityValue || 0),
          quantity_unit: root.dataset.unitQuantityUnit || '',
          reference_value: Number(root.dataset.unitReferenceValue || 1),
          reference_unit: root.dataset.unitReferenceUnit || '',
        },
      },
      regularPriceHTML: '',
    };
    pdpState.set(root, controller);

    const host = priceHost(controller);
    if (host && !host.querySelector('[data-ng-discount-main-price]')) {
      controller.regularPriceHTML = host.innerHTML;
    }

    const button = root.querySelector('[data-ng-discount-apply]');
    if (button) button.addEventListener('click', () => applyFromPdp(controller, button));

    return controller;
  }

  function priceHost(controller) {
    if (!controller.sectionId) return null;
    return document.getElementById(`price-${controller.sectionId}`);
  }

  function renderMainPrice(controller, active) {
    const host = priceHost(controller);
    if (!host) return;

    if (!active) {
      if (host.querySelector('[data-ng-discount-main-price]') && controller.regularPriceHTML) {
        host.innerHTML = controller.regularPriceHTML;
      }
      return;
    }

    if (!host.querySelector('[data-ng-discount-main-price]')) {
      controller.regularPriceHTML = host.innerHTML;
    }

    const regularPrice = Number(controller.variant.price || 0);
    if (!regularPrice) return;

    const discounted = promoPrice(regularPrice, controller.percent);
    let unitPriceHTML = '';
    const regularUnitPrice = Number(controller.variant.unit_price || 0);
    const measurement = controller.variant.unit_price_measurement || {};

    if (regularUnitPrice > 0 && measurement.reference_unit) {
      const promoUnitPrice = unitPriceFor(discounted, measurement, { unitPrice: regularUnitPrice, price: regularPrice });
      unitPriceHTML = unitPriceMarkup(
        promoUnitPrice,
        Number(measurement.reference_value || 1),
        measurement.reference_unit || ''
      );
    }

    // Vergleichspreis der aktuell gewählten Variante (nach Variantenwechsel aus dem Variant-Objekt)
    const uvp = Number(controller.variant.compare_at_price || 0);

    host.innerHTML = `
      <div class="price price--large nsg-main-price" data-ng-discount-main-price>
        <div class="price__container">
          <div class="nsg-main-price__row">
            ${srOnly(T.srSalePrice)}
            <span class="price-item nsg-main-price__current">
              ${money(discounted)}
            </span>
            ${srOnly(T.srRegularPrice)}
            <s class="price-item nsg-main-price__regular">
              ${uvp > regularPrice ? `${T.uvp} ${money(uvp)}` : money(regularPrice)}
            </s>
          </div>
          ${unitPriceHTML}
        </div>
      </div>
    `;
  }

  function renderPdp(active) {
    document.querySelectorAll(SELECTORS.pdp).forEach((root) => {
      const controller = pdpController(root);
      // Ausverkaufte Variante oder keine laufende Aktion: keine Box, kein Kampagnenpreis
      const variantAvailable = controller.variant.available !== false;
      const percent = controller.percent;
      root.hidden = !variantAvailable || !percent;
      if (root.hidden) {
        renderMainPrice(controller, false);
        return;
      }
      // Prozent und Code nur anpassen, wenn die Seite veraltet ist (sonst unverändert = kein Layout-Shift)
      const code = campaignCode();
      root.querySelectorAll('[data-ng-discount-percent-text]').forEach((node) => {
        if (node.textContent !== String(percent)) node.textContent = String(percent);
      });
      root.querySelectorAll('[data-ng-discount-code-text]').forEach((node) => {
        if (node.textContent !== code) node.textContent = code;
      });
      const inactiveState = root.querySelector('[data-ng-discount-inactive]');
      const activeState = root.querySelector('[data-ng-discount-active]');
      const errorState = root.querySelector('[data-ng-discount-error]');
      if (inactiveState) inactiveState.hidden = active;
      if (activeState) activeState.hidden = !active;
      if (errorState) errorState.hidden = true;
      renderMainPrice(controller, active);
    });
  }

  async function applyFromPdp(controller, button) {
    const errorState = controller.root.querySelector('[data-ng-discount-error]');
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = T.pdpLoading;

    try {
      await fetchState();
      const campaign = campaignCode();
      if (!campaign) throw new Error('Keine laufende Rabattaktion.');
      const codes = state.codes.map((entry) => entry.code);
      if (!codes.some((code) => code.toUpperCase() === campaign.toUpperCase())) codes.push(campaign);

      const { cart, targets } = await requestCodes(codes, { withSections: true });
      applySections(cart.sections, targets);
      if (!isActive()) throw new Error('Rabattcode wurde nicht übernommen.');
    } catch (error) {
      console.error('Rabattcode konnte nicht angewendet werden:', error);
      if (errorState) errorState.hidden = false;
    } finally {
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  document.addEventListener('variant:change', (event) => {
    const detail = event.detail || {};
    document.querySelectorAll(SELECTORS.pdp).forEach((root) => {
      const controller = pdpController(root);
      if (controller.sectionId && String(detail.sectionId) !== String(controller.sectionId)) return;
      if (detail.variant) controller.variant = detail.variant;
      const host = priceHost(controller);
      if (host) controller.regularPriceHTML = host.innerHTML;
    });
    // Box-Sichtbarkeit (ausverkauft) und Preisblock für die neue Variante
    renderPdp(isActive());
  });

  /* ---------- Gebinde-Picker (Produktseite) ---------- */

  function renderPicker(root, active) {
    root.classList.toggle('is-discount-active', active);

    root.querySelectorAll('[data-ng-discount-bundle-option]').forEach((option) => {
      // qty = Packungsgröße (Flaschen/Fläschchen, für „pro …“); Vergleich über die Referenz „Einzeln gekauft“
      const qty = Number(option.dataset.packQty || 1);
      const normalPrice = Number(option.dataset.price || 0);
      const promoPercent = percentFor(option.dataset.category);
      const effectivePrice = active ? promoPrice(normalPrice, promoPercent) : normalPrice;
      const comparisonTotal = referenceFor(option.dataset.parts, active && promoPercent > 0);
      const savings = Math.max(0, comparisonTotal - effectivePrice);
      const savingsPercent = comparisonTotal > 0 && savings > 0 ? Math.floor((savings * 100) / comparisonTotal) : 0;

      const priceEl = option.querySelector('[data-ng-bundle-price]');
      const unitEl = option.querySelector('[data-ng-bundle-unit-price]');
      const perBottleEl = option.querySelector('[data-ng-bundle-per-bottle]');
      const perUnitPriceEl = option.querySelector('[data-ng-bundle-per-unit-price]');
      const badge = option.querySelector('[data-ng-bundle-badge]');
      const savingsPercentEl = option.querySelector('[data-ng-bundle-savings-percent]');
      const popularEl = option.querySelector('[data-ng-bundle-popular]');
      const savingLine = option.querySelector('[data-ng-bundle-saving-line]');
      const savingCopy = option.querySelector('[data-ng-bundle-saving-copy]');

      if (priceEl) {
        priceEl.textContent = money(effectivePrice);
        priceEl.classList.toggle('is-promo', active && promoPercent > 0);
      }

      if (unitEl && qty > 0) {
        if (perBottleEl) perBottleEl.textContent = `${money(perBottle(effectivePrice, qty))} ${perUnitLabel(option)}`;
        // Grundpreis aus der eigenen Angabe der Variante; mit Code wie Shopify aus dem Rabattpreis berechnet.
        if (perUnitPriceEl) {
          const ownUnitPrice = Number(option.dataset.unitPrice || 0);
          const optionUnitPrice =
            effectivePrice === normalPrice
              ? ownUnitPrice
              : unitPriceFor(
                  effectivePrice,
                  {
                    quantity_value: option.dataset.unitQuantityValue,
                    quantity_unit: option.dataset.unitQuantityUnit,
                    reference_value: option.dataset.unitReferenceValue,
                    reference_unit: option.dataset.unitReferenceUnit,
                  },
                  { unitPrice: ownUnitPrice, price: normalPrice }
                );
          perUnitPriceEl.textContent = money(optionUnitPrice);
        }
        unitEl.hidden = false;
      }

      const showPercent = option.dataset.showSavingsBadge === 'true' && savingsPercent > 0;
      const isPopular = option.dataset.popular === 'true';

      if (savingsPercentEl) {
        savingsPercentEl.textContent = `${savingsPercent} ${T.savingsPercent}`;
        savingsPercentEl.hidden = !showPercent;
      }
      if (popularEl) popularEl.hidden = !isPopular;
      if (badge) badge.hidden = !(isPopular || showPercent);

      if (savingLine && savingCopy) {
        savingCopy.textContent = savings > 0 ? savingsCopy(comparisonTotal, savings) : '';
        savingLine.hidden = !(option.dataset.showSavingsLine === 'true' && savings > 0);
      }
    });
  }

  /* ---------- Gebinde-Auswahl im Quick-Add-Modal (Produktkarten) ---------- */

  function renderModal(root, active) {
    root.querySelectorAll('[data-ng-discount-bundle-modal-option]').forEach((option) => {
      const base = Number(option.dataset.price || 0);
      const percent = percentFor(option.dataset.category);
      const qty = Number(option.dataset.qty || 1);
      const current = active ? promoPrice(base, percent) : base;
      const reference = referenceFor(option.dataset.parts, active && percent > 0);

      const priceEl = option.querySelector('[data-ng-modal-price]');
      if (priceEl) priceEl.textContent = money(current);

      const unitEl = option.querySelector('[data-ng-modal-unit]');
      if (unitEl) unitEl.textContent = `${money(perBottle(current, qty))} ${perUnitLabel(option)}`;

      const savingEl = option.querySelector('[data-ng-modal-saving]');
      if (savingEl) {
        const amount = Math.max(0, reference - current);
        savingEl.textContent = amount > 0 ? savingsCopy(reference, amount) : '';
        savingEl.hidden = !(amount > 0);
      }
    });
  }

  function closeOpenBundleModals() {
    document.querySelectorAll('.nsg-bundle-quick-add-modal[open]').forEach((modal) => {
      if (typeof modal.hide === 'function') modal.hide();
      else modal.removeAttribute('open');
    });
  }

  document.addEventListener('click', (event) => {
    const option = event.target.closest('[data-ng-discount-bundle-modal-option]');
    if (!option || option.disabled) return;
    const root = option.closest(SELECTORS.modal);
    if (!root) return;
    root.querySelectorAll('[data-ng-discount-bundle-modal-option]').forEach((node) => {
      node.classList.toggle('is-selected', node === option);
    });
    const input = root.querySelector('[data-ng-bundle-modal-variant]');
    if (input) input.value = option.dataset.variantId;
  });

  /* ---------- Warenkorb: Rabattcodefeld ---------- */

  function formError(form, message) {
    const node = form.querySelector('[data-ng-discount-form-error]');
    if (!node) return;
    node.textContent = message || '';
    node.hidden = !message;
  }

  function formCodes(form) {
    return Array.from(form.querySelectorAll('[data-ng-discount-form-pills] .discount-pill[data-code]')).map((pill) =>
      String(pill.dataset.code || '').trim()
    );
  }

  async function applyFromCartForm(form) {
    const input = form.querySelector('[data-ng-discount-form-input]');
    const button = form.querySelector('[data-ng-discount-form-apply]');
    if (!input || !button || button.disabled) return;

    formError(form, '');
    const code = input.value.trim().toUpperCase();
    if (!code) {
      formError(form, T.cartErrorNoCode);
      return;
    }

    const existing = formCodes(form);
    if (existing.some((entry) => entry.toUpperCase() === code)) {
      formError(form, T.cartErrorAlreadyApplied);
      return;
    }

    const originalText = button.textContent;
    let dots = 0;
    let loading = true;
    button.disabled = true;
    const animate = () => {
      if (!loading) return;
      dots = (dots + 1) % 4;
      button.textContent = originalText + '.'.repeat(dots);
      setTimeout(animate, 400);
    };
    animate();

    try {
      const { cart, targets } = await requestCodes([...existing, code], { withSections: true });
      const entry = normalizeCodes(cart.discount_codes).find((item) => item.code.toUpperCase() === code);
      if (entry && entry.applicable) {
        applySections(cart.sections, targets);
      } else {
        // Ungültigen Code wieder entfernen, damit der Warenkorb sauber bleibt.
        await requestCodes(existing);
        formError(form, T.cartErrorInvalid);
      }
    } catch (error) {
      console.error('Rabattcode konnte nicht angewendet werden:', error);
      formError(form, T.cartErrorInvalid);
    } finally {
      loading = false;
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  async function removeFromCartForm(form, code) {
    formError(form, '');
    const remaining = formCodes(form).filter((entry) => entry.toUpperCase() !== String(code).toUpperCase());
    try {
      const { cart, targets } = await requestCodes(remaining, { withSections: true });
      applySections(cart.sections, targets);
    } catch (error) {
      console.error('Rabattcode konnte nicht entfernt werden:', error);
    }
  }

  document.addEventListener('click', (event) => {
    const applyButton = event.target.closest('[data-ng-discount-form-apply]');
    if (applyButton) {
      const form = applyButton.closest('[data-ng-discount-form]');
      if (form) applyFromCartForm(form);
      return;
    }
    const removeButton = event.target.closest('[data-ng-discount-remove]');
    if (removeButton) {
      const form = removeButton.closest('[data-ng-discount-form]');
      if (form) removeFromCartForm(form, removeButton.dataset.ngDiscountRemove);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const input = event.target.closest && event.target.closest('[data-ng-discount-form-input]');
    if (!input) return;
    event.preventDefault();
    const form = input.closest('[data-ng-discount-form]');
    if (form) applyFromCartForm(form);
  });

  /* ---------- Rendering & Synchronisierung ---------- */

  function renderAll() {
    renderAnnouncement();
    const active = isActive();
    renderCards(active);
    renderPdp(active);
    renderSavings(active);
    document.querySelectorAll(SELECTORS.picker).forEach((root) => renderPicker(root, active));
    document.querySelectorAll(SELECTORS.modal).forEach((root) => renderModal(root, active));
  }

  document.addEventListener('shopify:cart:discount-update', (event) => {
    if (event.detail && event.detail.source === 'ng-discount') return;
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise
        .then((result) => {
          if (result && result.cart && Array.isArray(result.cart.discountCodes)) setCodes(result.cart.discountCodes);
          else fetchState();
        })
        .catch(fetchState);
      return;
    }
    fetchState();
  });

  function syncIfStale() {
    if (Date.now() - lastSync >= FOCUS_SYNC_INTERVAL) fetchState();
  }

  document.addEventListener('shopify:section:load', renderAll);
  // Zurück-/Vor-Navigation aus dem Browser-Cache: Warenkorb kann sich geändert haben → sofort abfragen.
  // Beim normalen Laden fragt init() ab.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) fetchState();
  });
  window.addEventListener('focus', syncIfStale);

  function observeDynamicContent() {
    if (!document.body) return;
    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) =>
        Array.from(mutation.addedNodes || []).some(
          (node) =>
            node instanceof Element &&
            ((node.matches && node.matches(WATCHED)) || (node.querySelector && node.querySelector(WATCHED)))
        )
      );
      if (!relevant) return;
      clearTimeout(observerTimer);
      observerTimer = setTimeout(renderAll, 0);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function subscribeToCartUpdates() {
    const subscribeFn = typeof window.subscribe === 'function' ? window.subscribe : null;
    const events = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);
    if (!subscribeFn || !events || !events.cartUpdate) return;
    subscribeFn(events.cartUpdate, () => {
      closeOpenBundleModals();
      setTimeout(fetchState, 80);
    });
  }

  function init() {
    renderAll();
    observeDynamicContent();
    subscribeToCartUpdates();
    scheduleBoundary();
    fetchState();
  }

  window.NgDiscount = {
    get active() {
      return isActive();
    },
    get campaign() {
      return currentCampaign();
    },
    percentFor,
    get codes() {
      return state.codes.slice();
    },
    refresh: fetchState,
    promoPrice,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
